import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, TextField, Button, Stack, MenuItem, Alert } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/apiService';
import { Link as RouterLink } from 'react-router-dom';

const MyProfile: React.FC = () => {
  const { authenticated, login } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  const [handle, setHandle] = useState('');
  const [visibility, setVisibility] = useState<'public'|'private'>('private');
  const [bio, setBio] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!authenticated) { setLoading(false); return; }
      try {
        const resp = await apiService.getMyProfile();
        if (cancelled) return;
        const prof = resp?.data?.profile || null;
        if (prof) {
          setHandle(prof.handle || '');
          setVisibility((prof.visibility === 'public' ? 'public' : 'private'));
          setBio(prof.bio || '');
        }
      } catch (e: any) {
        // Not fatal; user may not have a profile yet
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authenticated]);

  const onSave = async () => {
    setError(null);
    setSavedMsg(null);
    if (!handle || handle.trim().length < 3) {
      setError('Handle must be at least 3 characters');
      return;
    }
    setSaving(true);
    try {
      const payload: any = { handle: handle.trim(), visibility, bio: bio || null };
      const resp = await apiService.upsertMyProfile(payload);
      if (resp?.data?.profile) {
        setSavedMsg('Profile saved');
      } else {
        setSavedMsg('Saved');
      }
    } catch (e: any) {
      const msg = e?.response?.data?.detail || 'Failed to save profile';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };


  const [zip, setZip] = useState<string>('');
  const [loc, setLoc] = useState<any | null>(null);
  const [locSaving, setLocSaving] = useState(false);
  const [locError, setLocError] = useState<string | null>(null);
  const [reps, setReps] = useState<any[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!authenticated) return;
      try {
        const resp = await apiService.getMyLocation();
        if (cancelled) return;
        const l = resp?.data?.location || null;
        setLoc(l);
        setZip((l?.zip || '') as string);
        setReps(Array.isArray(resp?.data?.representatives) ? resp.data.representatives : []);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [authenticated]);

  const onSaveLocation = async () => {
    setLocError(null);
    if (!zip || !/^\d{5,9}$/.test(zip)) {
      setLocError('Enter a valid 5 or 9 digit ZIP');
      return;
    }
    setLocSaving(true);
    try {
      const resp = await apiService.setMyLocation(zip);
      setLoc(resp?.data?.location || null);
      setReps(Array.isArray(resp?.data?.representatives) ? resp.data.representatives : []);
    } catch (e: any) {
      setLocError(e?.response?.data?.detail || 'Failed to set location');
    } finally {
      setLocSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>My Profile</Typography>
      {!authenticated ? (
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="textSecondary">Sign in to edit your profile.</Typography>
              <Button variant="contained" onClick={login}>Sign In</Button>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {savedMsg && (
            <Alert severity="success" sx={{ mb: 2 }}
              action={visibility === 'public' && handle ? (
                <Button color="inherit" size="small" component={RouterLink as any} to={`/u/${handle}`}>View Public</Button>
              ) : undefined}
            >{savedMsg}</Alert>
          )}
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <TextField
                  label="Handle"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  helperText="3+ chars; letters, numbers, '_' and '-' allowed"
                  inputProps={{ maxLength: 32 }}
                  disabled={loading || saving}
                />
                <TextField
                  select
                  label="Visibility"
                  value={visibility}
                  onChange={(e) => setVisibility((e.target.value as 'public'|'private') || 'private')}
                  disabled={loading || saving}
                >
                  <MenuItem value="private">Private</MenuItem>
                  <MenuItem value="public">Public</MenuItem>
                </TextField>
                <TextField
                  label="Bio"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  multiline
                  minRows={4}
                  placeholder="Share a short bio (optional)"
                  disabled={loading || saving}
                />
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={onSave} disabled={saving}>Save</Button>
                  {visibility === 'public' && handle && (
                    <Button component={RouterLink as any} to={`/u/${handle}`}>View Public Profile</Button>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {/* Location */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>My Location</Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Set your ZIP code to personalize your dashboard and show your representatives.
              </Typography>
              {locError && <Alert severity="error" sx={{ mb: 1 }}>{locError}</Alert>}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <TextField label="ZIP" value={zip} onChange={(e) => setZip(e.target.value)} size="small" sx={{ maxWidth: 200 }} />
                <Button variant="contained" onClick={onSaveLocation} disabled={locSaving}>Save ZIP</Button>
                {loc?.state && (
                  <Alert severity="success" sx={{ py: 0.5 }}>
                    Using location: {loc.state}{loc.district ? `-${loc.district}` : ''}
                  </Alert>
                )}
              </Stack>
              {reps.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>Your Reps</Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    {reps.map((r: any, idx: number) => (
                      <Alert key={idx} severity={r.role === 'senator' ? 'info' : 'success'} sx={{ py: 0.5 }}>
                        {r.name} ({r.party || '—'}) — {r.state}{r.district ? `-${r.district}` : ''}
                      </Alert>
                    ))}
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Calendar export */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Calendar Export</Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Download your hearings calendar (next 14 days) as an iCalendar file.
              </Typography>
              <Button
                variant="outlined"
                onClick={async () => {
                  try {
                    const resp = await apiService.getCalendarIcs();
                    const blob = new Blob([resp.data], { type: 'text/calendar' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'askthebill-calendar.ics';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                  } catch (e) {
                    // noop
                  }
                }}
              >
                Download .ics
              </Button>
              <Box sx={{ mt: 2 }}>
                <Button variant="outlined" component={RouterLink as any} to="/me/quiz/vote-alignment">Take the Vote Alignment Quiz</Button>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default MyProfile;
